import { LinkButton } from '@/components/ui';
import { ArrowRightIcon, BarbellIcon, FlameIcon, SparkIcon, WatchIcon } from '@/lib/icons';
import { DEMO_SESSION, DEMO_STREAK_WEEKS } from '@/data/demoSession';

const MARQUEE = [
  'TRACK IT. BEAT IT.',
  'SET. REP. REPEAT.',
  'NO EXCUSES, JUST REPS',
  'AN AI COACH FOR YOUR SPECIFIC NEED',
  '102.5 KG × 5 · PR',
  'LIFT2FIT',
];

/**
 * Hero: light-mode, one orange CTA, and the brand's own artwork — the distressed
 * sticker plus a real DOM preview of The Grid (never a screenshot, so it stays
 * crisp on any display).
 */
export function Hero() {
  return (
    <section id="top" className="relative overflow-hidden border-b border-hairline bg-page">
      <div aria-hidden="true" className="l2f-halftone pointer-events-none absolute inset-0 opacity-40" />
      <div className="relative mx-auto grid max-w-[1200px] 2xl:max-w-[1440px] min-[1920px]:max-w-[1720px] items-start gap-10 px-4 pb-14 pt-6 sm:px-6 lg:grid-cols-[1.14fr_0.86fr] lg:gap-6 lg:pb-16 lg:pt-4">
        {/* ---------------- copy ---------------- */}
        <div className="flex flex-col items-start gap-7">
          <p className="inline-flex items-center gap-2 rounded-pill border border-accent/40 bg-accent/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-accent lg:-mb-3">
            <FlameIcon className="text-sm" />
            Free forever for tracking — every kind of training
          </p>

          <h1 className="font-display text-[33px] font-black uppercase leading-[1.04] tracking-[-0.03em] text-ink sm:text-[50px] lg:pl-7 lg:text-[58px]">
            Stop logging in spreadsheets.
            <br />
            <span className="text-accent">Start training with an AI coach.</span>
          </h1>

          <ul className="flex max-w-[56ch] flex-col gap-2.5 text-body-lg text-ink-muted lg:pl-7">
            {[
              { icon: <BarbellIcon className="text-accent" />, lead: 'Log workouts', rest: 'sets, intervals, mobility — in one place.' },
              { icon: <WatchIcon className="text-accent" />, lead: 'One log, many devices', rest: 'web, phone and watch, syncing live.' },
              { icon: <SparkIcon className="text-accent" />, lead: 'AI coach', rest: 'track, analyze and specialize workouts.' },
            ].map((line) => (
              <li key={line.lead} className="flex items-start gap-3">
                <span className="mt-[5px] shrink-0 text-base">{line.icon}</span>
                <span>
                  <strong className="font-bold text-ink">{line.lead}</strong> — {line.rest}
                </span>
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap items-center gap-3">
            <LinkButton href="#/signup" size="lg">
              Start free — no card <ArrowRightIcon className="text-sm" />
            </LinkButton>
            <LinkButton href="#grid" variant="outline" size="lg">
              <BarbellIcon className="text-base" /> Try The Grid
            </LinkButton>
          </div>

          <dl className="flex flex-wrap items-start gap-x-7 gap-y-3 pt-2 sm:flex-nowrap">
            {[
              { label: 'One log', value: 'Web · iOS · Android · watch', icon: <WatchIcon className="text-accent" /> },
              { label: 'AI coach', value: 'A specialist for your track', icon: <SparkIcon className="text-accent" /> },
              { label: 'Streak', value: `${DEMO_STREAK_WEEKS} weeks running`, icon: <FlameIcon className="text-accent" /> },
            ].map((item) => (
              <div key={item.label} className="flex min-w-0 flex-col gap-1">
                <dt className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">
                  {item.icon}
                  {item.label}
                </dt>
                <dd className="l2f-tabular whitespace-nowrap font-mono text-[11px] font-bold text-ink">
                  {item.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>

        {/* ---------------- art ---------------- */}
        <div className="relative flex flex-col items-center pt-2 lg:items-start lg:pl-7 lg:pt-[74px]">
<>
            <span className="dark:hidden">
                        <img
            src="/brand/lift2fit-sticker-light.svg"
            alt="The Lift2Fit sticker emblem: the LIFT/2FIT wordmark whose aligned T\u2019s form a barbell, distressed like a worn t-shirt print"
            width={480}
            height={480}
            className="l2f-tilt-left l2f-logo-nudge w-[300px] mix-blend-multiply drop-shadow-[0_18px_40px_rgba(17,17,17,0.28)] sm:w-[380px] lg:w-[392px]"
            loading="eager"
          />
            </span>
            <span className="hidden dark:block">
                        <img
            src="/brand/lift2fit-sticker.svg"
            alt="The Lift2Fit sticker emblem: the LIFT/2FIT wordmark whose aligned T\u2019s form a barbell, distressed like a worn t-shirt print"
            width={480}
            height={480}
            className="l2f-tilt-left l2f-logo-nudge w-[300px] drop-shadow-[0_18px_40px_rgba(17,17,17,0.28)] sm:w-[380px] lg:w-[392px]"
            loading="eager"
          />
            </span>
          </>

          <div className="l2f-tilt-left-sm -mt-9 w-[300px] max-w-[94%] rounded-card border border-hairline bg-surface p-3 shadow-modal sm:w-[340px] lg:-ml-24 lg:-mt-40 lg:w-[330px]">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">
                The Grid · {DEMO_SESSION.label}
              </span>
              <span className="rounded-pill border border-accent bg-accent/10 px-2 py-[2px] text-[10px] font-bold uppercase tracking-[0.08em] text-accent">
                Week {DEMO_SESSION.week}
              </span>
            </div>
            <table className="w-full border-separate border-spacing-0">
              <thead>
                <tr>
                  {['Exercise', 'S1', 'S2', 'S3'].map((heading, index) => (
                    <th
                      key={heading}
                      scope="col"
                      className={`border-b border-hairline py-1 text-[9px] font-bold uppercase tracking-[0.1em] text-ink-muted ${
                        index === 0 ? 'text-left' : 'text-center'
                      }`}
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="l2f-tabular font-mono text-[11px]">
                {DEMO_SESSION.rows.slice(0, 3).map((row) => (
                  <tr key={row.id}>
                    <th scope="row" className="border-b border-hairline py-1.5 pr-2 text-left font-sans text-[11px] font-bold text-ink">
                      {row.movement}
                      {row.variant ? <span className="font-normal text-ink-muted"> {row.variant}</span> : null}
                    </th>
                    {[0, 1, 2].map((index) => {
                      const set = row.sets[index];
                      return (
                        <td key={index} className="border-b border-hairline py-1.5 text-center">
                          {set ? (
                            <span className={set.status === 'pr' ? 'font-bold text-accent' : 'text-ink'}>
                              {set.weightKg}×{set.reps}
                              {set.status === 'pr' ? <span aria-hidden="true">▲</span> : null}
                            </span>
                          ) : (
                            <span className="text-ink-muted">—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.08em] text-accent">
              ▲ PR detected automatically · e1RM tracked per rep range
            </p>
          </div>
        </div>
      </div>

      {/* ---------------- brand marquee ---------------- */}
      <div className="border-t border-hairline bg-brand-charcoal py-3 text-brand-white">
        <div className="flex overflow-hidden">
          <div className="l2f-marquee-track flex shrink-0 items-center gap-8 whitespace-nowrap pr-8">
            {[...MARQUEE, ...MARQUEE].map((phrase, index) => (
              <span key={`${phrase}-${index}`} className="flex items-center gap-8">
                <span className="font-display text-[13px] font-black uppercase tracking-[0.08em]">{phrase}</span>
                <span aria-hidden="true" className="text-accent">
                  ◆
                </span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
